import './SubClusterList.less';

import { DeleteOutlined, EditOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, Empty, Modal, notification, Table, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import copy from 'clipboard-copy';
import CreateNewSubClusterModal from '../CreateNewSubClusterModal/CreateNewSubClusterModal';
import SubClusterState from './SubClusterState/SubClusterState';
import { ReactComponent as OpenIcon } from 'images/open-icon.svg';
import {
  Instance,
  InstanceInfo,
  SubCluster,
  SubClusterStates,
} from 'types/cluster';
import { getClusterTypeName, getIU } from 'utils/cluster';
import useRequest from 'hooks/useRequest';
import {
  deleteSubCluster,
  connectSubCluster,
  disconnectSubCluster,
} from 'services/cluster';
import { SessionContext } from 'auth/SessionProvider';

interface SubClusterListProps {
  instance: Instance;
  requestCluster: () => Promise<any>;
  createButtonUi?: React.ReactNode;
  minimum?: boolean;
}

function SubClusterList({
  instance,
  requestCluster,
  createButtonUi = null,
  minimum = false,
}: SubClusterListProps) {
  let columns: ColumnProps<SubCluster>[];

  if (minimum) {
    columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Replicas',
        dataIndex: 'nodes',
        key: 'nodes',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render(_, subCluster) {
          let state = getSubClusterState(subCluster);
          return <SubClusterState state={state} />;
        },
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        render: size => getClusterTypeName(size),
      },
      {
        title: 'URL',
        key: 'incorta',
        render: (_, subCluster) => {
          return (
            <SubClusterIcnortaActions
              subCluster={subCluster}
              instance={instance}
              requestCluster={requestCluster}
            />
          );
        },
      },
    ];
  } else {
    columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Replicas',
        dataIndex: 'nodes',
        key: 'nodes',
        align: 'right',
      },
      {
        title: 'IU',
        dataIndex: 'iu',
        key: 'iu',
        align: 'right',
        render: (_, subCluster) => {
          return getIU(subCluster, subCluster.nodes, 'subcluster');
        },
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        render: size => getClusterTypeName(size),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render(_, subCluster) {
          let state = getSubClusterState(subCluster);
          return <SubClusterState state={state} />;
        },
      },
      {
        title: 'Incorta',
        key: 'incorta',
        render: (_, subCluster) => {
          return (
            <SubClusterIcnortaActions
              subCluster={subCluster}
              instance={instance}
              requestCluster={requestCluster}
            />
          );
        },
      },
      {
        title: '',
        key: 'actions',
        render: (_, subCluster) => {
          return (
            <SubClusterActions
              subCluster={subCluster}
              instance={instance}
              requestCluster={requestCluster}
            />
          );
        },
      },
    ];
  }

  return (
    <div className="SubClusterList">
      <Table
        size="small"
        className="SubClusterList__table"
        pagination={false}
        columns={columns}
        dataSource={instance.subClusters}
        rowKey="id"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No Analytics Subcluster created yet."
            >
              {createButtonUi}
            </Empty>
          ),
        }}
      />
    </div>
  );
}

interface SubClusterActionsProps {
  subCluster: SubCluster;
  instance: Instance;
  requestCluster: () => Promise<AxiosResponse<InstanceInfo>>;
}
function SubClusterActions({
  subCluster,
  instance,
  requestCluster,
}: SubClusterActionsProps) {
  const { user } = useContext(SessionContext);
  let [showModal, setShowModal] = useState(false);

  let showEdit = instance.clusterPolicy.subClusterPolicy.includes('update');
  let showDelete = instance.clusterPolicy.subClusterPolicy.includes('delete');

  let { makeRequest: deleteSubClusterRequest } = useRequest(
    useCallback(async () => {
      await deleteSubCluster({
        userId: user!.uuid,
        clusterName: instance.name,
        subClusterName: subCluster.name,
      });
      await requestCluster();
    }, [instance.name, requestCluster, subCluster.name, user]),
    { handleError: true },
  );

  function handleDelete() {
    Modal.confirm({
      title: `Delete ${subCluster.name} Sub Cluster`,
      content: 'Are you sure delete this Sub Cluster?',
      okText: 'Delete',
      cancelText: 'Cancel',
      centered: true,
      width: 620,
      async onOk() {
        await deleteSubClusterRequest();
        notification.success({
          message: 'Sub Cluster Deleted',
          description: `Successfully deleted ${subCluster.name} Sub Cluster.`,
        });
      },
    });
  }

  let state = getSubClusterState(subCluster);

  let {
    makeRequest: connectSubClusterRequest,
    isPending: connectSubClusterLoading,
  } = useRequest(
    useCallback(async () => {
      await connectSubCluster({
        userId: user!.uuid,
        clusterName: instance.name,
        subClusterName: subCluster.name,
      });
      await requestCluster();
    }, [instance.name, requestCluster, subCluster.name, user]),
    { handleError: true },
  );
  let {
    makeRequest: disconnectSubClusterRequest,
    isPending: disconnectSubClusterLoading,
  } = useRequest(
    useCallback(async () => {
      await disconnectSubCluster({
        userId: user!.uuid,
        clusterName: instance.name,
        subClusterName: subCluster.name,
      });
      await requestCluster();
    }, [instance.name, requestCluster, subCluster.name, user]),
    { handleError: true },
  );

  let loading =
    disconnectSubClusterLoading ||
    connectSubClusterLoading ||
    state === 'loading';

  return (
    <div className="SubClusterActions">
      {loading ? (
        <Button size="small" style={{ width: 100 }} loading></Button>
      ) : state === 'running' ? (
        <Button
          size="small"
          style={{ width: 100 }}
          onClick={() => disconnectSubClusterRequest()}
        >
          Disconnect
        </Button>
      ) : state === 'disconnected' ? (
        <Button
          size="small"
          style={{ width: 100 }}
          onClick={() => connectSubClusterRequest()}
        >
          Connect
        </Button>
      ) : null}

      {showEdit && (
        <Tooltip
          title={state === 'running' ? 'Edit' : 'Subcluster Must be running!'}
        >
          <button
            className="icon-btn"
            disabled={state !== 'running'}
            onClick={() => setShowModal(true)}
          >
            <EditOutlined />
          </button>
        </Tooltip>
      )}

      {showDelete && (
        <Tooltip title={'Remove Subcluster'}>
          <button className="icon-btn" onClick={handleDelete}>
            <DeleteOutlined />
          </button>
        </Tooltip>
      )}

      <Modal
        title="Edit Sub cluster"
        visible={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={645}
        centered
      >
        <CreateNewSubClusterModal
          onCancel={() => setShowModal(false)}
          instance={instance}
          subCluster={subCluster}
        />
      </Modal>
    </div>
  );
}

interface SubClusterIcnortaActionsProps {
  subCluster: SubCluster;
  instance: Instance;
  requestCluster: () => Promise<AxiosResponse<InstanceInfo>>;
}
function SubClusterIcnortaActions({
  subCluster,
  instance,
  requestCluster,
}: SubClusterIcnortaActionsProps) {
  let [copping, setCopping] = useState(false);

  let state = getSubClusterState(subCluster);

  useEffect(() => {
    if (copping) {
      let id = setTimeout(() => setCopping(false), 1000);
      return () => clearTimeout(id);
    }
  }, [copping]);

  return (
    <div className="SubClusterActions">
      <Tooltip title="Open Incorta">
        <a
          aria-disabled={state !== 'running'}
          href={subCluster.url}
          className="icon-btn"
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => {
            if (state !== 'running') {
              e.preventDefault();
            }
          }}
        >
          <OpenIcon className="text-icon" />
        </a>
      </Tooltip>

      <Tooltip title={copping ? 'Copied' : 'Copy Incorta URL'}>
        <button
          className="icon-btn"
          onClick={() => {
            copy(subCluster.url);
            setCopping(true);
          }}
        >
          <LinkOutlined />
        </button>
      </Tooltip>
    </div>
  );
}

function getSubClusterState(subCluster: SubCluster): SubClusterStates {
  if (subCluster.state === 'disconnected') {
    return 'disconnected';
  }
  return subCluster.status as any;
}

export default SubClusterList;
